import { BonusLabel } from 'api/Wallet/Types/BonusCampaignTypes'

import { isTickmillCompanyUKType } from '../../Clients/Types/TickmillCompanyTypes'
import { NameDto } from '../../Common/Types'
import { isPaymentProviderCategoryBank } from '../../PaymentProvider/Types/PaymentMethodCategory'
import { isTransactionWalletWithdrawalType } from '../../Wallet/Types/TransactionTypeDto'
import { CurrencyType, WalletDto } from '../../Wallet/Types/Wallet'
import { BonusType } from './BonusTransactionDto'
import { TransactionLinksDto } from './TransactionLinksDto'

export interface TransactionDto {
  id: string
  amount: number
  referenceId: string
  type: {
    id: number
    name: string
  }
  state: {
    id: number
    name: string
  }
  groupReference?: GroupReference
  groupReferences?: GroupReference[]
  linkedFrom: TransactionLinksDto[]
  linkedTo: TransactionLinksDto[]
  comment: string | null
  createdDate: string
  updatedDate: string
  reverseTransaction?: {
    id: string
    name: string
  }
  reversedTransactions?: {
    id: string
    name: string
  }[]
  wallet?: { id: string; name: string }
  walletTransactions: WalletTransaction[]
  tradingAccountTransactions: TradingAccount[]
}

export const isTransactionWithdrawalType = (data?: TransactionDto | null) => {
  return isTransactionWalletWithdrawalType(data?.type?.id)
}

export interface TransactionState {
  name: 'Pending'
}

export enum WalletDirectionTypeId {
  CREDIT = 200,
  DEBIT = 100,
}

export enum WalletType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
}
interface GroupReference {
  dateCreated: string
  dateUpdated: string
  id: string
  name: string
  type: NameDto
}

export interface WalletTransaction {
  id: string
  directionType: {
    id: number
    name: string // "Credit"
  }
  otherCurrency: NameDto<CurrencyType>
  amount: number
  bonusTransaction: {
    bonusLabel: BonusLabel
    bonusReserved: number
    bonusCampaign: { id: string; name: string }
    bonusTransactionStatus: NameDto
    bonusType: BonusType
    expiryDate: string
    id: string
    lotsRequired: number
    lotsTraded: number
    tradedProfit: number
  }
  wallet: Wallet
  currencyRate: number
  otherAmount: number
  paymentProviderTransaction: PaymentProviderDetails | null
  paymentAgentTransaction?: {
    account: string
    documentId: string
    id: string
    paymentAgent: { id: string; name: string }
    system: string
  }
}

export const isTransactionPaymentProviderDetailsCategoryBank = (
  data?: WalletTransaction | null
) => {
  return isPaymentProviderCategoryBank(
    data?.paymentProviderTransaction?.paymentProvider?.category?.id
  )
}

export const isTransactionWalletTickmillCompanyUK = (data?: WalletTransaction | null) => {
  return isTickmillCompanyUKType(data?.wallet?.company?.id)
}

export const isTransactionNotConnectedToPspService = (data?: WalletTransaction | null) => {
  return data?.paymentProviderTransaction?.isConnectedToService === false
}

interface PaymentProviderDetails {
  id: string
  providerReference: null | unknown
  paymentProvider: {
    id: string
    name: string
    externalName: string
    category: NameDto
  }
  clientBankAccountDetails: null | {
    bankAccount: string
    bankCode: string
    client: NameDto<string>
    data: {
      accountName: string
      alternativeBankAddress: null
      alternativeBankName: null
      bankDetails: {
        Address: string
        Code: string
        CodeSort: string
        Id: string
        Name: string
      }
      comment: string
      document: NameDto
    }
    status: NameDto
  }
  isConnectedToService: boolean
  currency: NameDto<CurrencyType>
  request: {
    [key: string]: string | null | CurrencyType
    user_ip: string
    swift: string
    bank_name: string
    user_zip: string
    method_id: string
    reference: string
    user_city: string
    company_id: string
    user_email: string
    user_phone: string
    language_id: string
    user_gender: string
    user_address: string
    wallet_amount: string
    payment_amount: string
    user_birthdate: string
    user_last_name: string
    wallet_type_id: string
    user_country_id: string
    user_first_name: string
    external_account: null
    wallet_currency_id: CurrencyType
    payment_currency_id: CurrencyType
  } | null
  response: {
    update?: {
      externalAccount?: string | null
      providerReference?: string | null
      providerResponse?: string | null
      paymentCurrencyId?: string | null
      details: {
        bankAddress: string | null
        bankBranch: string | null
        bankCode: string | null
        bankName: string | null
        billingCityCode: string | null
        billingCityName: string | null
        billingStateCode: string | null
        billingStateName: string | null
        cardMonth: string | null
        cardYear: string | null
        nameOfAccount: string | null
      } | null
    }
    external_account?: string | null
  } | null
}

export enum TransactionPaymentProviderType {
  Bank = 'Bank',
  Skrill = 'Skrill',
}

interface Wallet extends WalletDto {
  company?: NameDto<number>
  reserved: number
}

export interface TradingAccount {
  id: string
  directionType: {
    id: number
    name: string // "Debit"
  }
  client: {
    id: string
    name: string
  }
  tickmillCompany: {
    id: number
    name: string
  }
  amount: number
  tradingAccount: {
    id: string
    name: string
  }
  wallet: {
    id: string
    name: string
  }
  ibCommisionTransaction: {
    id: string
    introducingBroker: {
      id: string
      name: string
    }
    lotCount: number
  }
}

export enum TransactionKey {
  type = 'type.name',
  reference = 'referenceId',
  groupReference = 'groupReferences[0].name',
  state = 'state.name',
  clientCount = 'clientCount',
  walletCount = 'walletCount',
  amount = 'amount',
  date = 'date',
  walletDebitAmount = 'walletDebitAmount',
  walletCreditAmount = 'walletCreditAmount',
  lotAmount = 'lotAmount',
  currency = 'currency.id',
  tickmillCompany = 'tickmillCompany.name',
  ibWallet = 'wallet.name',
  ibNumber = 'introducingBroker.name',
  walletTransactionWallet = 'walletTransactions.[0].wallet.name',
  walletTransactionWalletClient = 'walletTransactions.[0].wallet.client.name',
  walletTransactionAmount = 'walletTransactions.[0].amount',
  walletTransactionWalletCurrency = 'walletTransactions.[0].wallet.currency.id',
  walletTransactionWalletCompany = 'walletTransactions.[0].wallet.company.name',
  walletTransactionWalletWalletType = 'walletTransactions.[0].wallet.walletType.name',
  walletTransactionCurrencyRate = 'walletTransactions.[0].currencyRate',
  walletTransactionPaymentProviderTransaction = 'walletTransactions.[0].paymentProviderTransaction.paymentProvider.name',
  walletTransactionPaymentProviderTransactionUserCountry = 'walletTransactions?.[0]?.paymentProviderTransaction?.request?.user_country_id',

  // Debit
  walletDebitTransactionWallet = 'walletDebitTransactions.[0].wallet.name',
  walletDebitTransactionWalletClient = 'walletDebitTransactions.[0].wallet.client.name',
  walletDebitTransactionAmount = 'walletDebitTransactions.[0].amount',
  walletDebitTransactionWalletCurrency = 'walletDebitTransactions.[0].wallet.currency.id',
  walletDebitTransactionWalletCompany = 'walletDebitTransactions.[0].wallet.company.name',
  walletDebitTransactionWalletWalletType = 'walletDebitTransactions.[0].wallet.walletType.name',
  walletDebitTransactionCurrencyRate = 'walletDebitTransactions.[0].currencyRate',
  walletDebitTransactionPaymentProviderTransaction = 'walletDebitTransactions.[0].paymentProviderTransaction.paymentProvider.name',

  // Credit
  walletCreditTransactionWallet = 'walletCreditTransactions.[0].wallet.name',
  walletCreditTransactionWalletClient = 'walletCreditTransactions.[0].wallet.client.name',
  walletCreditTransactionAmount = 'walletCreditTransactions.[0].amount',
  walletCreditTransactionWalletCurrency = 'walletCreditTransactions.[0].wallet.currency.id',
  walletCreditTransactionWalletCompany = 'walletCreditTransactions.[0].wallet.company.name',
  walletCreditTransactionWalletWalletType = 'walletCreditTransactions.[0].wallet.walletType.name',
  walletCreditTransactionCurrencyRate = 'walletCreditTransactions.[0].currencyRate',
  walletCreditTransactionPaymentProviderTransaction = 'walletCreditTransactions.[0].paymentProviderTransaction.paymentProvider.name',

  tradingAccountTransactionTradingAccount = 'tradingAccountTransactions.[0].tradingAccount.name',

  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
}

export enum TransactionFilterKey {
  type = 'TransactionTypeId',
  amount = 'Amount',
  reference = 'Reference',
  groupReference = 'GroupReferenceName',
  state = 'TransactionStateId',
  date = 'Date',

  walletTransactionWallet = 'WalletId',
  walletTransactionWalletClient = 'Client',
  walletTransactionWalletCompany = 'TickmillCompany',
  ibNumber = 'IbNumber',
  walletTransactionPaymentProviderTransaction = 'PaymentProvider',

  tradingAccountTransactionTradingAccount = 'TaId',

  countryId = 'ClientCountryId',

  isInternalTestAccount = 'IsInternalTestAccount',
  createdDate = 'DateCreated',
  updatedDate = 'DateUpdated',
  createdDateTime = 'DateTimeCreated',
  updatedDateTime = 'DateTimeUpdated',

  assignee = 'Assignee',

  walletTransactionWalletPaymentAgent = 'PaymentAgentId',
}

export enum TransactionSortKey {
  createdDate = 'CreatedDate',
  updatedDate = 'UpdatedDate',
}
